import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/stores/auth';
import type { UserRole } from '@/types';

interface ProtectedRouteProps {
  children: React.ReactNode;
  roles?: UserRole[];
  requireAuth?: boolean;
  fallbackPath?: string;
}

// Hook per controllo ruoli - Export separato per Fast Refresh
export function useRoleCheck() {
  const { hasRole, hasAnyRole, isAdmin } = useAuth();

  return {
    hasRole,
    hasAnyRole,
    isAdmin,
    canViewAdminSection: () => hasAnyRole(['organo_tecnico', 'ref_informatico', 'presidente', 'vice_presidente']),
    canManageUsers: () => hasAnyRole(['ref_informatico', 'presidente']),
    canViewStatistics: () => hasAnyRole(['organo_tecnico', 'ref_informatico', 'presidente', 'vice_presidente']),
    canInsertCRA01: () => hasAnyRole(['organo_tecnico', 'ref_informatico', 'presidente', 'vice_presidente']),
    canInsertRapportino: () => true, // All authenticated users can insert rapportini
  };
}

// Componente principale ProtectedRoute
export function ProtectedRoute({
  children,
  roles = [],
  requireAuth = true,
  fallbackPath = '/login',
}: ProtectedRouteProps) {
  const location = useLocation();
const { isAuthenticated, hasAnyRole, isLoading } = useAuth();

// Utente veramente autenticato solo se lo store dice true e ho un token
const tokenLS = typeof window !== 'undefined' ? localStorage.getItem('arma4you.token') : null;
const reallyAuthenticated = isAuthenticated && !!tokenLS;

  // Show loading while checking authentication
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  // Check authentication requirement
if (requireAuth && !reallyAuthenticated) {
    // Redirect to login with current location for redirect after login
    const redirectTo = location.pathname + location.search;
    return (
      <Navigate 
        to={`${fallbackPath}?redirectTo=${encodeURIComponent(redirectTo)}`} 
        replace 
      />
    );
  }

  // Check role requirements
  if (roles.length > 0 && !hasAnyRole(roles)) {
    // User doesn't have required roles, redirect to unauthorized page or home
    return <Navigate to="/unauthorized" replace />;
  }

  // All checks passed, render children
  return <>{children}</>;
}

// Role-based component wrapper
interface RoleGateProps {
  children: React.ReactNode;
  roles: UserRole[];
  fallback?: React.ReactNode;
  requireAll?: boolean; // If true, user must have ALL roles, not just one
}

export function RoleGate({
  children,
  roles,
  fallback = null,
  requireAll = false,
}: RoleGateProps) {
  const { hasRole, hasAnyRole } = useAuth();

  const hasAccess = requireAll 
    ? roles.every(role => hasRole(role))
    : hasAnyRole(roles);

  if (!hasAccess) {
    return <>{fallback}</>;
  }

  return <>{children}</>;
}

// Admin gate component
interface AdminGateProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

export function AdminGate({
  children,
  fallback = null,
}: AdminGateProps) {
  return (
    <RoleGate
      roles={['organo_tecnico', 'ref_informatico', 'presidente', 'vice_presidente']}
      fallback={fallback}
    >
      {children}
    </RoleGate>
  );
}

// HOC for protecting components
export function withRoleProtection<P extends object>(
  Component: React.ComponentType<P>,
  requiredRoles: UserRole[],
  fallback?: React.ComponentType
) {
  const ProtectedComponent: React.FC<P> = (props) => {
    const { hasAnyRole } = useAuth();

    if (!hasAnyRole(requiredRoles)) {
      if (fallback) {
        const FallbackComponent = fallback;
        return <FallbackComponent />;
      }
      return null;
    }

    return <Component {...props} />;
  };

  ProtectedComponent.displayName = `withRoleProtection(${Component.displayName || Component.name})`;

  return ProtectedComponent;
}

// Unauthorized page component
export function UnauthorizedPage() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
            Accesso non autorizzato
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Non hai i permessi necessari per accedere a questa pagina.
          </p>
          <div className="mt-6">
            <button
              onClick={() => window.history.back()}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              Torna indietro
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Default export per compatibilità
export default ProtectedRoute;